import React, { useRef, useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, Send, RefreshCw, CheckCircle2, Building2, TrendingUp, X } from 'lucide-react';
import { Image, useColorModeValue } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { sendOtpInEmailOrNo, userProfile } from '../../../redux/actions/user';
import { authLogin } from '../../../redux/actions/authentication';
import Logo from '../../../assets/img/zeronine.png';
import { sendOtp as sendOtpAction, forgetPassword } from '../../../redux/actions/user';

function SignIn() {
   const [isDark, setIsDark] = useState(false);

   // Original state variables from your code
   const [email, setEmail] = useState('');
   const [emailForOtp, setEmailForOtp] = useState('');
   const [forgotPasswordOtp, setForgotPasswordOtp] = useState(''); // Separate state for forgot password OTP
   const [twoFactorOtp, setTwoFactorOtp] = useState(''); // Separate state for 2FA OTP
   const otpRef = useRef(null);
   const [newPassword, setNewPassword] = useState('');
   const [showOtpFields, setShowOtpFields] = useState(false);
   const [showPassword, setShowPassword] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [otpShow, setOtpShow] = useState(false);
   const [otpMethod, setOtpMethod] = useState('email');
   const [otpSent, setOtpSent] = useState(false);
   const [isCheckingAuth, setIsCheckingAuth] = useState(false);
   const overlayBg = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(0, 0, 0, 0.8)');
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

   // Forgot password modal state
   const [showForgotModal, setShowForgotModal] = useState(false);

   // Form state for react-hook-form replacement
   const [formData, setFormData] = useState({ email: '', password: '', otp: '' });
   const [errors, setErrors] = useState({});

   // Your original useEffect
   useEffect(() => {
      const checkAuthAndRedirect = async () => {
         const tokenData = localStorage.getItem('authTokenCustomer');

         if (tokenData) {
            try {
               const parsedToken = JSON.parse(tokenData);
               await dispatch(userProfile());
               navigate('/admin/default');
            } catch (error) {
               localStorage.removeItem('authTokenCustomer');
            }
         }
         setIsCheckingAuth(false);
      };

      checkAuthAndRedirect();

      const authChannel = new BroadcastChannel('auth');
      authChannel.onmessage = (e) => {
         if (e.data.type === 'LOGIN') {
            checkAuthAndRedirect();
         }
         if (e.data.type === 'LOGOUT') {
            navigate('/auth/sign-in');
         }
      };

      return () => {
         authChannel.close();
      };
   }, []);

   // Your original onSubmit function
   const onSubmit = async (data) => {
      setIsLoading(true);
      try {
         if (otpShow) {
            const loginData = { ...data, otp: twoFactorOtp }; // Use twoFactorOtp for login
            const res = await dispatch(authLogin(navigate, loginData));
            if (res.code === 1) {
               await dispatch(userProfile());
            }
         } else {
            const res = await dispatch(authLogin(navigate, data));
            setEmailForOtp(data.email);
            if (res.data.web2Factor) {
               setOtpShow(true);
            } else {
               await dispatch(userProfile());
            }
         }
      } catch (error) {
         console.error('Login error:', error);
      } finally {
         setIsLoading(false);
      }
   };

   // Your original handleKeyDown function
   const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
         handleSubmit();
      }
   };

   // Your original sendOtp function
   const sendOtp = () => {
      const Data = { email: email };
      dispatch(sendOtpAction(Data));
      setShowOtpFields(true);
      otpRef.current?.focus();
   };

   // Your original resetPassword function
   const resetPassword = () => {
      if (forgotPasswordOtp && newPassword) {
         // Use forgotPasswordOtp
         const formData = { email: email, password: newPassword, otp: Number(forgotPasswordOtp) };
         dispatch(forgetPassword(formData));
      }
      setShowForgotModal(false);
      setEmail('');
      setForgotPasswordOtp(''); // Reset forgot password OTP
      setNewPassword('');
      setShowOtpFields(false);
   };

   // Your original handleSendOtp function
   const handleSendOtp = async () => {
      setOtpSent(true);
      const otpdata = {
         email: emailForOtp,
         sendType: otpMethod === 'email' ? 0 : otpMethod === 'mobile' ? 1 : 2,
      };
      await dispatch(sendOtpInEmailOrNo(otpdata));
   };

   // Handle OTP input for 2FA
   const handleTwoFactorOtpChange = (index, value) => {
      if (value) {
         const newOtp = twoFactorOtp.split('');
         newOtp[index] = value;
         const updatedOtp = newOtp.join('');
         setTwoFactorOtp(updatedOtp);
         if (errors.otp) setErrors({ ...errors, otp: '' });

         // Auto-focus next input
         if (index < 5) {
            document.getElementById(`twoFactor-otp-${index + 1}`)?.focus();
         }
      }
   };

   // Handle OTP input for forgot password
   const handleForgotPasswordOtpChange = (index, value) => {
      if (value) {
         const newOtp = forgotPasswordOtp.split('');
         newOtp[index] = value;
         const updatedOtp = newOtp.join('');
         setForgotPasswordOtp(updatedOtp);
         if (errors.otp) setErrors({ ...errors, otp: '' });

         // Auto-focus next input
         if (index < 5) {
            document.getElementById(`forgot-otp-${index + 1}`)?.focus();
         }
      }
   };

   // Handle backspace for 2FA OTP
   const handleTwoFactorOtpBackspace = (index, e) => {
      if (e.key === 'Backspace' && !twoFactorOtp[index] && index > 0) {
         document.getElementById(`twoFactor-otp-${index - 1}`)?.focus();
      } else if (e.key === 'Backspace' && twoFactorOtp[index]) {
         // Clear current field and stay there
         const newOtp = twoFactorOtp.split('');
         newOtp[index] = '';
         setTwoFactorOtp(newOtp.join(''));
      }
   };

   // Handle backspace for forgot password OTP
   const handleForgotPasswordOtpBackspace = (index, e) => {
      if (e.key === 'Backspace' && !forgotPasswordOtp[index] && index > 0) {
         document.getElementById(`forgot-otp-${index - 1}`)?.focus();
      } else if (e.key === 'Backspace' && forgotPasswordOtp[index]) {
         // Clear current field and stay there
         const newOtp = forgotPasswordOtp.split('');
         newOtp[index] = '';
         setForgotPasswordOtp(newOtp.join(''));
      }
   };

   // Handle paste for 2FA OTP
   const handleTwoFactorOtpPaste = (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      setTwoFactorOtp(pastedData);
      if (errors.otp) setErrors({ ...errors, otp: '' });

      // Focus last filled box
      const nextIndex = Math.min(pastedData.length, 5);
      document.getElementById(`twoFactor-otp-${nextIndex}`)?.focus();
   };

   // Handle paste for forgot password OTP
   const handleForgotPasswordOtpPaste = (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      setForgotPasswordOtp(pastedData);
      if (errors.otp) setErrors({ ...errors, otp: '' });

      // Focus last filled box
      const nextIndex = Math.min(pastedData.length, 5);
      document.getElementById(`forgot-otp-${nextIndex}`)?.focus();
   };

   // Handle form submission
   const handleSubmit = () => {
      const newErrors = {};

      if (!formData.email) {
         newErrors.email = 'Email is required';
      }
      if (!formData.password) {
         newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
         newErrors.password = 'Password must be at least 6 characters';
      }
      if (otpShow && !twoFactorOtp) {
         newErrors.otp = 'OTP is required';
      } else if (otpShow && !/^[0-9]{6}$/.test(twoFactorOtp)) {
         newErrors.otp = 'Please enter a valid 6-digit OTP';
      }

      setErrors(newErrors);

      if (Object.keys(newErrors).length === 0) {
         onSubmit(formData);
      }
   };

   if (isCheckingAuth) {
      return (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm">
            <RefreshCw className="w-12 h-12 animate-spin text-indigo-600" />
         </div>
      );
   }

   return (
      <div
         className={`min-h-screen transition-colors duration-300 ${
            isDark
               ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
               : 'bg-gradient-to-br from-blue-50 via-white to-indigo-50'
         }`}>
         {/* Loading overlay */}
         {isLoading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
               <div className={`p-8 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <RefreshCw className="w-16 h-16 animate-spin text-indigo-600 mx-auto" />
               </div>
            </div>
         )}

         {/* Top Bar: Color Mode Toggle (Left) + Logo (Right) */}
         <div className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center p-6 sm:p-8">
            {/* Color Mode Toggle - Left */}
            <button
               onClick={() => setIsDark(!isDark)}
               className={`p-3 rounded-xl transition-all duration-300 ${
                  isDark
                     ? 'bg-slate-800 text-yellow-400 hover:bg-slate-700 shadow-lg shadow-slate-900/50'
                     : 'bg-white text-slate-700 hover:bg-slate-50 shadow-lg shadow-slate-200/50'
               }`}>
               {isDark ? '☀️' : '🌙'}
            </button>

            {/* Logo - Right (You can replace this with your Logo component) */}
            <Link to={'/'}>
               <div
                  className={`px-4 py-2 rounded-xl cursor-pointer ${
                     isDark
                        ? 'bg-slate-800/80 backdrop-blur-sm'
                        : 'bg-white/80 backdrop-blur-sm shadow-lg shadow-slate-200/50'
                  }`}>
                  <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                     {' '}
                     <Image src={Logo} alt="Zeronine Logo" w="150px" />
                  </span>
               </div>
            </Link>
         </div>

         <div className="min-h-screen flex">
            {/* Left Side - Branding */}
            <div
               className={`hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden ${
                  isDark
                     ? 'bg-gradient-to-br from-indigo-600 to-purple-700'
                     : 'bg-gradient-to-br from-indigo-600 to-blue-700'
               }`}>
               <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
                  <div
                     className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"
                     style={{ animationDelay: '1s' }}></div>
               </div>

               <div className="relative z-10 flex flex-col justify-between px-12 xl:px-20 py-12 text-white w-full">
                  <div className="flex-1 flex flex-col justify-center">
                     <h2 className="text-4xl xl:text-5xl font-bold mb-6 leading-tight">
                        Streamline Your
                        <br />
                        Financial Operations
                     </h2>

                     <p className="text-lg text-white/90 mb-12 max-w-md">
                        Powerful accounting software for modern businesses. Manage invoices, expenses, and reports with
                        ease.
                     </p>

                     <div className="space-y-6">
                        <div className="flex items-start gap-4">
                           <div className="p-2 bg-white/20 rounded-lg mt-1">
                              <CheckCircle2 className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="font-semibold mb-1">Real-time Analytics</h3>
                              <p className="text-white/80 text-sm">
                                 Track your business performance with live dashboards
                              </p>
                           </div>
                        </div>

                        <div className="flex items-start gap-4">
                           <div className="p-2 bg-white/20 rounded-lg mt-1">
                              <TrendingUp className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="font-semibold mb-1">Smart Automation</h3>
                              <p className="text-white/80 text-sm">Automate recurring tasks and save valuable time</p>
                           </div>
                        </div>

                        <div className="flex items-start gap-4">
                           <div className="p-2 bg-white/20 rounded-lg mt-1">
                              <Lock className="w-5 h-5" />
                           </div>
                           <div>
                              <h3 className="font-semibold mb-1">Bank-level Security</h3>
                              <p className="text-white/80 text-sm">
                                 Your financial data is protected with enterprise-grade encryption
                              </p>
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* App Store Buttons at Bottom */}
                  <div className="mt-12">
                     <h6 className="font-semibold mb-4 text-sm text-white/90">Download Our App</h6>
                     <div className="flex flex-row gap-3 flex-wrap">
                        <a
                           href="https://play.google.com/store/apps/details?id=com.ss.zeronine.app"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300">
                           <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                              <path
                                 d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z"
                                 fill="#32BBFF"
                              />
                              <path d="M14.5 12.707l3.842 3.842-10.25 5.902 6.408-9.744z" fill="#F4B400" />
                              <path
                                 d="M18.342 16.549l3.654-2.103c.394-.227.658-.594.658-1.063 0-.469-.264-.836-.658-1.063l-3.654-2.103-3.842 3.841 3.842 3.491z"
                                 fill="#FF5B57"
                              />
                              <path d="M8.092 1.348L18.342 7.25l-3.842 3.842L8.092 1.348z" fill="#60D669" />
                           </svg>
                           <div className="text-left">
                              <p className="text-xs text-white/70">GET IT ON</p>
                              <p className="text-xs font-semibold text-white">Google Play</p>
                           </div>
                        </a>

                        <a
                           href="https://apps.apple.com/in/app/09-ai/id6747242947"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300">
                           <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                           </svg>
                           <div className="text-left">
                              <p className="text-xs text-white/70">Download on the</p>
                              <p className="text-xs font-semibold text-white">App Store</p>
                           </div>
                        </a>
                     </div>
                  </div>
               </div>
            </div>

            {/* Right Side - Sign In Form */}
            <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 sm:p-8">
               <div className="w-full max-w-md">
                  {/* Sign In Card */}
                  <div
                     className={`rounded-2xl shadow-2xl p-8 sm:p-10 transition-colors duration-300 ${
                        isDark
                           ? 'bg-slate-800/50 backdrop-blur-xl border border-slate-700/50'
                           : 'bg-white/80 backdrop-blur-xl border border-white/20'
                     }`}>
                     <div className="mb-8">
                        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                           Welcome To Zeronine
                        </h2>
                        <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                           Enter your email and password to sign in!
                        </p>
                     </div>

                     <div className="space-y-5">
                        {/* Email Input */}
                        <div>
                           <label
                              className={`block text-sm font-medium mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-slate-700'
                              }`}>
                              Email <span className="text-red-500">*</span>
                           </label>
                           <div className="relative">
                              <Mail
                                 className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${
                                    isDark ? 'text-slate-500' : 'text-slate-400'
                                 }`}
                              />
                              <input
                                 type="email"
                                 value={formData.email}
                                 onChange={(e) => {
                                    setFormData({ ...formData, email: e.target.value });
                                    setEmailForOtp(e.target.value);
                                    if (errors.email) setErrors({ ...errors, email: '' });
                                 }}
                                 onKeyDown={handleKeyDown}
                                 placeholder="Email"
                                 className={`w-full pl-12 pr-4 py-3.5 rounded-xl transition-all duration-200 ${
                                    isDark
                                       ? 'bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                       : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                 } ${errors.email ? 'border-red-500' : ''}`}
                              />
                           </div>
                           {errors.email && <p className="mt-1.5 text-sm text-red-500">{errors.email}</p>}
                        </div>

                        {/* Password Input */}
                        <div>
                           <label
                              className={`block text-sm font-medium mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-slate-700'
                              }`}>
                              Password <span className="text-red-500">*</span>
                           </label>
                           <div className="relative">
                              <Lock
                                 className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${
                                    isDark ? 'text-slate-500' : 'text-slate-400'
                                 }`}
                              />
                              <input
                                 type={showPassword ? 'text' : 'password'}
                                 value={formData.password}
                                 onChange={(e) => {
                                    setFormData({ ...formData, password: e.target.value });
                                    if (errors.password) setErrors({ ...errors, password: '' });
                                 }}
                                 onKeyDown={handleKeyDown}
                                 placeholder="Min. 6 characters"
                                 className={`w-full pl-12 pr-12 py-3.5 rounded-xl transition-all duration-200 ${
                                    isDark
                                       ? 'bg-slate-900/50 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                       : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                 } ${errors.password ? 'border-red-500' : ''}`}
                              />
                              <button
                                 type="button"
                                 onClick={() => setShowPassword(!showPassword)}
                                 className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${
                                    isDark
                                       ? 'text-slate-500 hover:text-slate-300'
                                       : 'text-slate-400 hover:text-slate-600'
                                 }`}>
                                 {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                           </div>
                           {errors.password && <p className="mt-1.5 text-sm text-red-500">{errors.password}</p>}
                        </div>

                        {/* OTP Section for 2FA */}
                        {otpShow && (
                           <div
                              className={`p-5 rounded-xl space-y-4 ${
                                 isDark
                                    ? 'bg-slate-900/50 border border-slate-700'
                                    : 'bg-indigo-50 border border-indigo-100'
                              }`}>
                              <div>
                                 <label
                                    className={`block text-sm font-medium mb-3 ${
                                       isDark ? 'text-slate-300' : 'text-slate-700'
                                    }`}>
                                    Choose OTP Method
                                 </label>

                                 <div className="flex items-center justify-between mb-4">
                                    <div className="flex gap-3">
                                       {['email', 'mobile', 'whatsapp'].map((method) => (
                                          <button
                                             key={method}
                                             type="button"
                                             onClick={() => setOtpMethod(method)}
                                             className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                                                otpMethod === method
                                                   ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                                                   : isDark
                                                     ? 'bg-slate-800 text-slate-400 hover:text-slate-300'
                                                     : 'bg-white text-slate-600 hover:text-slate-900'
                                             }`}>
                                             {method.charAt(0).toUpperCase() + method.slice(1)}
                                          </button>
                                       ))}
                                    </div>
                                 </div>
                                 <button
                                    type="button"
                                    onClick={handleSendOtp}
                                    className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                                       isDark
                                          ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                          : 'bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-200'
                                    }`}>
                                    {otpSent ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                                    {otpSent ? 'Resend OTP' : 'Send OTP'}
                                 </button>
                              </div>

                              <div>
                                 <label
                                    className={`block text-sm font-medium mb-2 ${
                                       isDark ? 'text-slate-300' : 'text-slate-700'
                                    }`}>
                                    Enter OTP
                                 </label>
                                 <div className="flex gap-2 justify-center">
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                       <input
                                          key={index}
                                          id={`twoFactor-otp-${index}`}
                                          type="text"
                                          maxLength={1}
                                          value={twoFactorOtp[index] || ''}
                                          onChange={(e) => {
                                             const value = e.target.value.replace(/\D/g, '');
                                             handleTwoFactorOtpChange(index, value);
                                          }}
                                          onKeyDown={(e) => {
                                             handleTwoFactorOtpBackspace(index, e);
                                             if (e.key === 'Enter') {
                                                handleKeyDown(e);
                                             }
                                          }}
                                          onPaste={handleTwoFactorOtpPaste}
                                          className={`w-12 h-14 text-center text-xl font-bold rounded-xl transition-all duration-200 ${
                                             isDark
                                                ? 'bg-slate-900/50 border-2 border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                                : 'bg-white border-2 border-slate-200 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                          } ${errors.otp ? 'border-red-500' : ''}`}
                                       />
                                    ))}
                                 </div>
                                 {errors.otp && <p className="mt-1.5 text-sm text-red-500 text-center">{errors.otp}</p>}
                              </div>
                           </div>
                        )}

                        {/* Submit Button */}
                        <button
                           type="button"
                           onClick={handleSubmit}
                           disabled={isLoading}
                           className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                              isLoading
                                 ? 'bg-slate-400 cursor-not-allowed'
                                 : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-lg shadow-indigo-600/30 hover:shadow-xl hover:shadow-indigo-600/40 hover:-translate-y-0.5'
                           }`}>
                           {isLoading ? (
                              <>
                                 <RefreshCw className="w-5 h-5 animate-spin" />
                                 Signing in...
                              </>
                           ) : (
                              'Sign In'
                           )}
                        </button>

                        <div
                           className={`pt-5 text-center text-sm border-t ${
                              isDark ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-600'
                           }`}>
                           Not registered yet?{' '}
                           <button
                              type="button"
                              onClick={() => (window.location.href = '/#pricing')}
                              className={`font-semibold transition-colors ${
                                 isDark
                                    ? 'text-indigo-400 hover:text-indigo-300'
                                    : 'text-indigo-600 hover:text-indigo-700'
                              }`}>
                              Create an Account
                           </button>{' '}
                           Or{' '}
                           <button
                              type="button"
                              onClick={() => setShowForgotModal(true)}
                              className={`font-semibold transition-colors ${
                                 isDark
                                    ? 'text-indigo-400 hover:text-indigo-300'
                                    : 'text-indigo-600 hover:text-indigo-700'
                              }`}>
                              Forgot Password
                           </button>
                           ?
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         {/* Forgot Password Modal */}
         {showForgotModal && (
            <div
               className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
               onClick={() => setShowForgotModal(false)}>
               <div
                  className={`w-full max-w-md rounded-2xl shadow-2xl p-8 ${
                     isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'
                  }`}
                  onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-6">
                     <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Forgot Password
                     </h3>
                     <button
                        onClick={() => setShowForgotModal(false)}
                        className={`p-2 rounded-lg transition-colors ${
                           isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600'
                        }`}>
                        <X className="w-5 h-5" />
                     </button>
                  </div>

                  <div className="space-y-4">
                     <div className="flex gap-3">
                        <input
                           type="email"
                           value={email}
                           onChange={(e) => setEmail(e.target.value.toLowerCase())}
                           placeholder="Enter your email"
                           autoFocus
                           className={`flex-1 px-4 py-3 rounded-xl transition-all duration-200 ${
                              isDark
                                 ? 'bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none'
                                 : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none'
                           }`}
                        />
                        <button
                           type="button"
                           onClick={sendOtp}
                           className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap">
                           Send OTP
                        </button>
                     </div>

                     {showOtpFields && (
                        <>
                           <div>
                              <label
                                 className={`block text-sm font-medium mb-2 ${
                                    isDark ? 'text-slate-300' : 'text-slate-700'
                                 }`}>
                                 Enter OTP
                              </label>
                              <div className="flex gap-2 justify-center">
                                 {[0, 1, 2, 3, 4, 5].map((index) => (
                                    <input
                                       key={index}
                                       id={`forgot-otp-${index}`}
                                       type="text"
                                       maxLength={1}
                                       value={forgotPasswordOtp[index] || ''}
                                       onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, '');
                                          handleForgotPasswordOtpChange(index, value);
                                       }}
                                       onKeyDown={(e) => {
                                          handleForgotPasswordOtpBackspace(index, e);
                                          if (e.key === 'Enter') {
                                             handleKeyDown(e);
                                          }
                                       }}
                                       onPaste={handleForgotPasswordOtpPaste}
                                       className={`w-12 h-14 text-center text-xl font-bold rounded-xl transition-all duration-200 ${
                                          isDark
                                             ? 'bg-slate-900/50 border-2 border-slate-700 text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                             : 'bg-white border-2 border-slate-200 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none'
                                       } ${errors.otp ? 'border-red-500' : ''}`}
                                    />
                                 ))}
                              </div>
                              {errors.otp && <p className="mt-1.5 text-sm text-red-500 text-center">{errors.otp}</p>}
                           </div>
                           <div className="relative">
                              <input
                                 type={showPassword ? 'text' : 'password'}
                                 value={newPassword}
                                 onChange={(e) => setNewPassword(e.target.value)}
                                 placeholder="Enter New Password"
                                 className={`w-full px-4 py-3 pr-12 rounded-xl transition-all duration-200 ${
                                    isDark
                                       ? 'bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none'
                                       : 'bg-white border-2 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none'
                                 }`}
                              />
                              <button
                                 type="button"
                                 onClick={() => setShowPassword(!showPassword)}
                                 className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${
                                    isDark
                                       ? 'text-slate-500 hover:text-slate-300'
                                       : 'text-slate-400 hover:text-slate-600'
                                 }`}>
                                 {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                           </div>
                        </>
                     )}
                  </div>

                  <div className="flex gap-3 mt-6">
                     {email && forgotPasswordOtp && newPassword && (
                        <button
                           onClick={resetPassword}
                           className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all">
                           Reset Password
                        </button>
                     )}
                     <button
                        onClick={() => {
                           setShowForgotModal(false);
                           setEmail('');
                           setForgotPasswordOtp('');
                           setNewPassword('');
                           setShowOtpFields(false);
                        }}
                        className={`${
                           email && forgotPasswordOtp && newPassword ? 'flex-1' : 'w-full'
                        } py-3 rounded-xl font-semibold transition-all ${
                           isDark
                              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}>
                        Cancel
                     </button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}

export default SignIn;
